import {
  ASSISTANT_PENDING_STOPS_KEY,
  ASSISTANT_PENDING_DISPATCHES_KEY,
  onSessionRemoved,
} from "@/shared/lib/token/token"
import {
  cancelAssistantGeneration,
  getAssistantConversation,
} from "./assistant.api"
import { forgetWatchedAssistantRequest } from "./assistant.completion-notifications"

const STORAGE_KEY = ASSISTANT_PENDING_STOPS_KEY
const DISPATCH_KEY = ASSISTANT_PENDING_DISPATCHES_KEY
const ACCEPTANCE_MS = 5 * 60 * 1000
// Covers delayed uploads/preparation without retrying a request that was never accepted forever.
const RETENTION_MS = 60 * 60 * 1000

type PendingStop = {
  conversationId: string
  requestId: string
  runId?: string
  requestedAt: number
}

const fallback = new Map<string, PendingStop[]>()
// A successful write of one key cannot make another key's stale persisted copy authoritative.
const dirtyStorageKeys = new Set<string>()
const workers = new Map<string, Promise<boolean>>()

const read = (key = STORAGE_KEY): PendingStop[] => {
  if (dirtyStorageKeys.has(key) && fallback.has(key)) return fallback.get(key)!
  try {
    const stored: unknown = JSON.parse(sessionStorage.getItem(key) ?? "[]")
    const entries = Array.isArray(stored)
      ? stored.filter(
          (entry): entry is PendingStop =>
            typeof entry?.conversationId === "string" &&
            typeof entry?.requestId === "string" &&
            typeof entry?.requestedAt === "number" &&
            (entry.runId === undefined || typeof entry.runId === "string")
        )
      : []
    fallback.set(key, entries)
    return entries
  } catch {
    dirtyStorageKeys.add(key)
    return fallback.get(key) ?? []
  }
}

const write = (entries: PendingStop[], key = STORAGE_KEY) => {
  fallback.set(key, entries)
  try {
    sessionStorage.setItem(key, JSON.stringify(entries))
    dirtyStorageKeys.delete(key)
  } catch {
    dirtyStorageKeys.add(key)
    // Storage may be unavailable. The running tab can still carry out Stop in memory.
  }
}

export const forgetPendingAssistantStop = (requestId: string) =>
  write(read().filter((entry) => entry.requestId !== requestId))

export const bindPendingAssistantStop = (requestId: string, runId: string) => {
  for (const key of [STORAGE_KEY, DISPATCH_KEY]) {
    write(
      read(key).map((entry) =>
        entry.requestId === requestId ? { ...entry, runId } : entry
      ),
      key
    )
  }
}

const runStop = async (requestId: string, onFailure?: () => void) => {
  let reportedFailure = false
  for (;;) {
    const entry = read().find((item) => item.requestId === requestId)
    // Done, logout, or another worker can remove the intent while this one is awaiting I/O.
    if (!entry) return true
    if (Date.now() - entry.requestedAt >= RETENTION_MS) {
      forgetPendingAssistantStop(requestId)
      onFailure?.()
      return false
    }
    try {
      if (entry.runId) {
        await cancelAssistantGeneration(entry.conversationId, entry.runId)
        // false means that exact run has already ended. Never fall back to cancelling a new run.
        forgetPendingAssistantStop(requestId)
        return true
      }
      const transcript = await getAssistantConversation({
        conversationId: entry.conversationId,
      })
      if (transcript.finishedRequestIds?.includes(requestId)) {
        finishAssistantRequest(requestId)
        return true
      }
      if (transcript.activeRun?.requestId === requestId) {
        bindPendingAssistantStop(requestId, transcript.activeRun.id)
        continue
      }
    } catch (caught) {
      const status = (caught as { response?: { status?: number } })?.response
        ?.status
      if (status === 403 || status === 404) {
        forgetPendingAssistantStop(requestId)
        return true
      }
      if (!reportedFailure) {
        reportedFailure = true
        onFailure?.()
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
}

const ensureWorker = (requestId: string, onFailure?: () => void) => {
  const existing = workers.get(requestId)
  if (existing) return existing
  const worker = runStop(requestId, onFailure).finally(() =>
    workers.delete(requestId)
  )
  workers.set(requestId, worker)
  return worker
}

/** Only IDs and a timestamp are stored; prompts, documents, and credentials stay out of storage. */
export const requestAssistantStop = (
  conversationId: string,
  requestId: string,
  runId?: string,
  onFailure?: () => void
) => {
  forgetWatchedAssistantRequest(requestId)
  const entries = read()
  const previous = entries.find((entry) => entry.requestId === requestId)
  write([
    ...entries.filter((entry) => entry.requestId !== requestId),
    {
      conversationId,
      requestId,
      runId: runId ?? previous?.runId,
      requestedAt: previous?.requestedAt ?? Date.now(),
    },
  ])
  return ensureWorker(requestId, onFailure)
}

/** Restart pending Stop delivery after a reload, even if a different page is open. */
export const resumePendingAssistantStops = (onSettled?: () => void) => {
  for (const entry of read()) {
    void ensureWorker(entry.requestId).then(() => onSettled?.())
  }
}

/** Dispatch IDs keep a returned page polling even before an active run is first exposed. */
export const rememberAssistantDispatch = (
  conversationId: string,
  requestId: string
) =>
  write(
    [
      ...read(DISPATCH_KEY).filter((entry) => entry.requestId !== requestId),
      { conversationId, requestId, requestedAt: Date.now() },
    ],
    DISPATCH_KEY
  )

export const pendingAssistantDispatches = (conversationId: string) => {
  const stored = read(DISPATCH_KEY)
  const live = stored.filter(
    (entry) => Date.now() - entry.requestedAt < ACCEPTANCE_MS
  )
  if (stored.length !== live.length) write(live, DISPATCH_KEY)
  return live.filter((entry) => entry.conversationId === conversationId)
}

export const finishAssistantRequest = (requestId: string) => {
  forgetPendingAssistantStop(requestId)
  write(
    read(DISPATCH_KEY).filter((entry) => entry.requestId !== requestId),
    DISPATCH_KEY
  )
}

/** Called on session teardown, including when browser storage was unavailable. */
export const clearPendingAssistantRequests = () => {
  fallback.clear()
  fallback.set(STORAGE_KEY, [])
  fallback.set(DISPATCH_KEY, [])
  dirtyStorageKeys.clear()
  for (const key of [STORAGE_KEY, DISPATCH_KEY]) {
    try {
      sessionStorage.removeItem(key)
    } catch {
      dirtyStorageKeys.add(key)
    }
  }
}

onSessionRemoved(clearPendingAssistantRequests)
